/**
 * Batch processing utility using requestAnimationFrame (rAF) for optimal
 * performance. This ensures that UI updates are synchronized with the
 * browser's paint cycle, preventing jank.
 */

/**
 * Interface for batch processor that manages rAF-based flushing of accumulated data.
 * The processor runs on requestAnimationFrame and calls the flush callback at optimal intervals.
 */
export interface BatchProcessor {
  /**
   * Starts the batch processing loop using requestAnimationFrame.
   * Will call the flush callback synchronized with browser paint cycles until stopped.
   */
  start: () => void;

  /**
   * Stops the batch processing loop and performs a final flush.
   * Ensures no data is lost when streaming completes.
   */
  stop: () => void;

  /**
   * Checks if the batch processor is currently running.
   * Used to prevent multiple processors from running simultaneously.
   */
  isRunning: () => boolean;
}

/**
 * Configuration for batch processor behavior and error handling.
 */
export interface BatchProcessorConfig {
  /**
   * Callback function to execute on each flush interval.
   * This should handle moving data from buffers to React state.
   */
  onFlush: () => void;

  /**
   * Optional error handler for flush callback failures.
   * Allows graceful error handling without stopping the processor.
   */
  onError?: (error: Error) => void;
}

/**
 * Creates a batch processor that calls a flush function using requestAnimationFrame.
 *
 * Performance rationale: By using requestAnimationFrame instead of setInterval,
 * we synchronize updates with the browser's paint cycle, eliminating jank and
 * providing smooth 60fps updates that feel real-time to users while dramatically
 * reducing React rendering overhead.
 *
 * @param config Configuration object with flush callback and error handler
 * @returns A BatchProcessor instance that can be started and stopped
 */
export function createBatchProcessor(
  config: BatchProcessorConfig,
): BatchProcessor {
  let animationFrameId: number | null = null;
  let isRunning = false;

  const processLoop = () => {
    if (!isRunning) return;

    try {
      config.onFlush();
    } catch (error) {
      const errorToReport =
        error instanceof Error
          ? error
          : new Error('Unknown batch processing error');
      config.onError?.(errorToReport);
    }

    // Schedule the next flush for the next animation frame
    animationFrameId = requestAnimationFrame(processLoop);
  };

  return {
    start: () => {
      if (isRunning) return;
      isRunning = true;
      animationFrameId = requestAnimationFrame(processLoop);
    },

    stop: () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      isRunning = false;

      // Perform one final flush to clear any remaining tokens
      try {
        config.onFlush();
      } catch (error) {
        const errorToReport =
          error instanceof Error
            ? error
            : new Error('Unknown final flush error');
        config.onError?.(errorToReport);
      }
    },

    isRunning: () => isRunning,
  };
}
